import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Grid } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';

import { useResetPassword } from '../../api-calls/auth.queries';
import { routes } from '../../constants';
import { resetPassword as resetPasswordValidation } from '../../validation/schemas';
import Button from '../../components/Button';
import * as T from '../../components/Typography';
import Error from '../../components/Error';
import colors from '../../theme/colors';
import logoWhiteBg from '../../components/assets/logo-navy.png';
import Notification from '../../components/Notification';
import { ControllerTextField } from '../../components/Inputs/Textfield';

interface ResetPasswordFormInputs {
  email: string;
}

const ResetPassword: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean | null>(null);

  const { control, handleSubmit, getValues } = useForm<ResetPasswordFormInputs>(
    {
      resolver: yupResolver(resetPasswordValidation)
    }
  );

  const { mutateAsync, isPending } = useResetPassword();

  const onSubmit = async (data: ResetPasswordFormInputs) => {
    const { email } = data;

    try {
      await mutateAsync({ email });
      setSuccess(true);
    } catch (err) {
      setError('Failed to change password.');
    }
  };

  if (error) {
    return <Error />;
  }

  return (
    <Grid
      container
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <Grid
        container
        maxWidth={'384px'}
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <img src={logoWhiteBg} alt="account verified" width="118" height="48" />
        <T.H4
          mt="6"
          mb="2"
          ta="center"
          color={colors.neutral.shades.shade700}
        >
          RESET YOUR PASSWORD
        </T.H4>
        <T.TextSmallRegular
          ta="center"
          mb={6}
          center
          color={colors.neutral.shades.shade600}
        >
          Enter your email to receive a password reset link.
        </T.TextSmallRegular>
      </Grid>
      <form onSubmit={handleSubmit(onSubmit)} style={{width: '100%', maxWidth: '384px'}}>
        <Grid item>
          <ControllerTextField
            control={control}
            label="Email address"
            name="email"
            placeholder="example@domain.com"
            type="email"
          />
        </Grid>
        {success && (
          <Notification
            type="success"
            message={`A password reset link has been sent to ${getValues('email')}. Check your inbox to proceed.`}
          />
        )}
        <Grid item mt={2}>
          <Button
            type="submit"
            variant="blue"
            loading={isPending}
            disabled={isPending}
            mt={5}
          >
            {isPending ? 'Loading...' : 'Send reset link'}
          </Button>
        </Grid>
      </form>
      <Box>
        <T.TextSmallRegular
          ta="center"
          mt="4"
          color={colors.neutral.shades.shade500}
        >
          Remembered your password?{' '}
          <T.Link
            to={routes.LOGIN}
            decoration="none"
            color={colors.blue.shades.shade700}
          >
            Log in here
          </T.Link>
        </T.TextSmallRegular>
      </Box>
    </Grid>
  );
};

export default ResetPassword;
